/** Response Rules legacy pane — Live under Automations Compat Soft≠ invent Soft disappear. Soft≠Live. */
import { useState, useMemo } from "react";
import { html, F, Ico, KV, Card, Field, Input, Alert, go } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import {
  dispatchRulesOf,
  candidatePlanImportBody,
  draftPlanFromLiveRows,
  softResponseErrorOf,
} from "./response-live.js";
export { softResponseErrorOf };

function safeRulesPath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__response_rules_status") return "/response/rules/status";
    if (op === "get__dispatch-rules") {
      const lim = (opts && opts.query && opts.query.limit) || 100;
      return `/dispatch-rules?limit=${encodeURIComponent(lim)}`;
    }
    if (op === "post__dispatch-rules_import") return "/dispatch-rules/import";
    if (op === "patch__dispatch-rules_p") {
      const id = (opts && opts.pathParams && opts.pathParams[0]) || "";
      return `/dispatch-rules/${encodeURIComponent(id)}`;
    }
    return "";
  }
}

/** Pure: seat chips from Live rules posture — fail-closed. */
export function responseRulesSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const max = Number(lim.list_max);
  const def = Number(lim.list_default);
  return {
    listAllowed: p.list_allowed === true,
    mutateAllowed: p.mutate_allowed === true,
    importAllowed: p.import_allowed === true,
    localDraftAllowed: p.local_draft_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    enabled: p.enabled === true,
    listLimit:
      Number.isFinite(def) && def > 0
        ? Math.min(def, Number.isFinite(max) && max > 0 ? max : 500)
        : 100,
  };
}

/** Pure: clamp Live rules paint — no Soft fill beyond source. */
export function rulesRowsClampOf(rows, maxN) {
  const list = Array.isArray(rows) ? rows : [];
  const n = Number(maxN);
  const cap = Number.isFinite(n) && n > 0 ? Math.floor(n) : 100;
  return list.slice(0, cap);
}

/** Pure: operator-facing when line Soft≠ invent wire paths. */
export function whenSay(r) {
  const row = r && typeof r === "object" ? r : {};
  const thr = row.threshold != null ? String(row.threshold) : "";
  const ev = String(row.event_type || row.when || row.trigger || "").trim();
  if (ev && thr) return ev + " · thr " + thr;
  if (ev) return ev;
  if (thr) return "threshold " + thr;
  return "—";
}

/** Pure: action kind honesty Soft≠ invent. */
export function kindSay(r) {
  const row = r && typeof r === "object" ? r : {};
  return String(row.kind || row.action_kind || row.channel || "dispatch").trim() || "dispatch";
}

export function diffPlan(live, candidate){
  const plan = Array.isArray(candidate) ? candidate : [];
  const out = [];
  plan.forEach(c=>{
    const l = live.find(x=>x.id===c.id);
    if(!l) out.push({ id:c.id, name:c.name, tag:"added", tone:"", say:"threshold " + c.threshold });
    else if(l.threshold !== c.threshold)
      out.push({ id:c.id, name:c.name, tag:"changed", tone:"warn", say:l.threshold + " → " + c.threshold });
  });
  live.forEach(l=>{
    if(!plan.some(c=>c.id===l.id))
      out.push({ id:l.id, name:l.name, tag:"removed", tone:"bad", say:"not in candidate plan" });
  });
  return out;
}

export function Rules({ toast }){
  const posture = useApi(safeRulesPath("get__response_rules_status"));
  const post = posture.data || {};
  const seat = responseRulesSeatOf(posture.loading ? null : post);
  const live = useApi(
    seat.listAllowed
      ? safeRulesPath("get__dispatch-rules", { query: { limit: seat.listLimit } })
      : "",
  );
  const rawRows = useMemo(() => dispatchRulesOf(live.data), [live.data]);
  const rows = useMemo(
    () => rulesRowsClampOf(rawRows, seat.listLimit),
    [rawRows, seat.listLimit],
  );
  const [dep,setDep]   = useState(null);
  const [why,setWhy]   = useState("");
  const [diff,setDiff] = useState(null);
  const [draft,setDraft] = useState([]);
  const act = useAction();

  const certifiedCount = (live.data && live.data.certified_count) || rows.filter(r=>r.certified).length;
  const revMeta = rows.length
    ? (rows.length + " rules · " + certifiedCount + " certified")
    : seat.listAllowed
      ? "No rules loaded yet"
      : "list sealed";

  const reRead = () => {
    posture.reload && posture.reload();
    live.reload && live.reload();
    toast("Rules refreshed", "go");
  };

  const confirmDeprecate = async (id) => {
    if (!seat.mutateAllowed) {
      toast("You cannot change rules on this seat", "warn");
      return;
    }
    if(why.trim().length < 4){ toast("Give a reason of at least four characters", "warn"); return; }
    const out = await act.run(
      safeRulesPath("patch__dispatch-rules_p", { pathParams: [id] }),
      { method: "PATCH", body: { enabled: false, reason: why.trim() } },
    );
    if (out) {
      toast("Deprecated · " + id, "warn");
      setDep(null); setWhy("");
      live.reload && live.reload();
    } else toast(softResponseErrorOf(act.error || "Deprecate refused"), "bad");
  };

  const enable = async (r) => {
    if (!seat.mutateAllowed) {
      toast("You cannot change rules on this seat", "warn");
      return;
    }
    const out = await act.run(
      safeRulesPath("patch__dispatch-rules_p", { pathParams: [r.id] }),
      { method: "PATCH", body: { enabled: true, reason: "CMC Response · enable" } },
    );
    if (out) {
      toast("Enabled · " + r.name, "go");
      live.reload && live.reload();
    } else toast(softResponseErrorOf(act.error || "Enable refused"), "bad");
  };

  const draftFromLive = () => {
    if (!seat.localDraftAllowed) {
      toast("Drafting rules is sealed for this seat", "warn");
      return;
    }
    const next = draftPlanFromLiveRows(rows);
    setDraft(next);
    setDiff(null);
    toast(next.length
      ? ("Draft from current rules · " + next.length)
      : "Table empty — nothing to draft", next.length ? "go" : "warn");
  };

  const compare = () => {
    if (!seat.localDraftAllowed) {
      toast("Compare is sealed for this seat", "warn");
      return;
    }
    if (!draft.length) {
      toast("Draft from current rules first", "warn");
      return;
    }
    const d = diffPlan(rows, draft);
    setDiff(d);
    toast(d.length
      ? "Draft differs in " + d.length + " rules"
      : "Draft matches current table", d.length ? "warn" : "go");
  };

  const doImport = async () => {
    if (!seat.importAllowed) {
      toast("Import needs a manager seat", "warn");
      return;
    }
    if (!draft.length) {
      toast("No draft to import", "warn");
      return;
    }
    const body = candidatePlanImportBody(draft);
    const out = await act.run(safeRulesPath("post__dispatch-rules_import"), { method: "POST", body });
    if (out) {
      toast("Imported · " + ((out.imported && out.imported.length) || body.rules.length) + " rules", "go");
      setDiff(null);
      live.reload && live.reload();
    } else toast(softResponseErrorOf(act.error || "Import refused"), "bad");
  };

  const scopeSay = seat.listAllowed
    ? "Review what triggers a dispatch, draft from the live table, then import when ready."
    : "Rules need a manager seat. Use Board for your assigned jobs.";

  return html`
    <${F} data-cmc-residual="page184-rules">
      <${Alert} tone="info" title="Dispatch rules"
        say=${scopeSay}/>
      <${Alert} tone="warn" title="Legacy compatibility · not Safety Reflex SoT"
        say="Business plan stays with AI Decision Contract. Deterministic emergency lives under Automations → Safety Reflex. This table is compatibility / certified dispatch only Soft≠ invent second brain."/>
      ${act.envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
        : null}
      ${!posture.loading && !seat.enabled
        ? html`<${Alert} tone="warn" title="Rules seat closed" say=${softResponseErrorOf(seat.detail || "Rules closed")}/>`
        : null}
      ${!posture.loading && seat.enabled && !seat.listAllowed
        ? html`<${Alert} tone="warn" title="Rules manager-only"
            say=${softResponseErrorOf(seat.detail || "Your role cannot list dispatch rules.")}/>`
        : null}
      ${live.error ? html`<${Alert} tone="warn" title="Could not refresh rules" say=${softResponseErrorOf(live.error)}/>` : null}

      <div className="btn-row resp-desk__acts">
        <p className="hint" style=${{ margin:0 }}>
          role <b>${seat.role || "—"}</b>
          · list ${seat.listAllowed ? "open" : "sealed"}
          · change ${seat.mutateAllowed ? "open" : "sealed"}
          · import ${seat.importAllowed ? "open" : "sealed"}
          · draft ${seat.localDraftAllowed ? "open" : "sealed"}
          · limit ${seat.listLimit}
        </p>
        <button type="button" className="btn btn--ghost btn--sm"
          disabled=${!seat.reReadAllowed && !posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/> Refresh</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/response/board")}>
          Board</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/response/roster")}>
          Roster</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick=${()=>go("/automations")}>
          Safety Reflex · Automations</button>
      </div>

      <${Card} title="What triggers a dispatch" meta="certified table">
        <${KV} k="ruleset revision" v=${revMeta}/>
        <table className="tbl" style=${{ marginTop:10 }}>
          <thead><tr><th>Rule</th><th>When</th><th>Sends</th><th>Priority</th><th>Rev</th><th>Certified</th><th/></tr></thead>
          <tbody>
            ${!seat.listAllowed
              ? html`<tr><td colSpan="7"><p className="note" style=${{ margin:0 }}>List sealed.</p></td></tr>`
              : !rows.length
                ? html`<tr><td colSpan="7"><p className="note" style=${{ margin:0 }}>${live.loading || posture.loading ? "Reading…" : "No dispatch rules loaded — empty is OK."}</p></td></tr>`
                : null}
            ${seat.listAllowed ? rows.map(r=> html`<${F} key=${r.id}>
              <tr data-off=${r.enabled?"0":"1"}>
                <td><b>${r.name}</b><br/><code style=${{ color:"var(--fg-4)" }}>${r.id}</code></td>
                <td>${whenSay(r)}</td>
                <td><span className="chip" data-tone=${r.kind==="sentinel_nexus"?"warn":""}>${r.action}</span>
                  <br/><em style=${{ fontSize:"var(--t-label)", color:"var(--fg-4)" }}>${kindSay(r)}</em></td>
                <td><code>${r.prio}</code></td>
                <td><code>r${r.rev}</code></td>
                <td>${r.certified
                  ? html`<span className="good">${r.certified}</span>`
                  : html`<span className=${r.drift?"warn":""}>${r.drift?"drifted since sign-off":r.reason||"never"}</span>`}</td>
                <td style=${{ textAlign:"right" }}>
                  ${!seat.mutateAllowed
                    ? html`<span className="note">sealed</span>`
                    : r.enabled
                      ? html`<button className="btn btn--sm btn--ghost" type="button" disabled=${act.busy}
                          onClick=${()=>{ setDep(dep===r.id?null:r.id); setWhy(""); }}>
                          ${dep===r.id ? "Close" : "Deprecate"}</button>`
                      : html`<button className="btn btn--sm btn--ghost" type="button" disabled=${act.busy}
                          onClick=${()=>enable(r)}>Enable</button>`}
                </td>
              </tr>
              ${dep===r.id && seat.mutateAllowed && html`
                <tr>
                  <td colSpan=${7}>
                    <${Field} label="Reason for deprecating" bad=${why.length>0 && why.trim().length<4}
                        hint="At least four characters. Deprecating also clears certification.">
                      <${Input} icon="book" autoFocus placeholder="why this rule stops firing"
                        value=${why} onChange=${e=>setWhy(e.target.value)}
                        bad=${why.length>0 && why.trim().length<4}/>
                    <//>
                    <div className="btn-row">
                      <button className="btn btn--danger btn--sm" type="button"
                        style=${ why.trim().length<4 ? { opacity:.45 } : null }
                        disabled=${act.busy}
                        onClick=${()=>confirmDeprecate(r.id)}>Deprecate</button>
                      <button className="btn btn--ghost btn--sm" type="button"
                        onClick=${()=>{ setDep(null); setWhy(""); }}>Cancel</button>
                    </div>
                  </td>
                </tr>`}
            <//>`) : null}
          </tbody>
        </table>
      <//>

      <${Card} title="Import a draft" meta=${draft.length ? (draft.length + " draft rules") : "start from table"} cls="mt"
          sub="Draft starts from the current table — no brochure defaults.">
        <div className="btn-row">
          <button className="btn btn--ghost" type="button" onClick=${draftFromLive}
            disabled=${live.loading||!seat.localDraftAllowed}>
            <${Ico} d="refresh" w=${13}/> Draft from table</button>
          <button className="btn btn--ghost" type="button" onClick=${compare}
            disabled=${!draft.length||!seat.localDraftAllowed}>Compare with table</button>
          <button className="btn btn--go" type="button" onClick=${doImport}
            disabled=${!diff||!draft.length||act.busy||!seat.importAllowed}
            style=${ (!diff||!draft.length||!seat.importAllowed) ? { opacity:.4, cursor:"not-allowed" } : null }>
            Import</button>
        </div>
        <p className="hint">Compare stays on this page. Import posts only when you confirm.</p>
        ${!seat.localDraftAllowed
          ? html`<p className="note">Draft is sealed.</p>`
          : !draft.length
            ? html`<p className="note">No draft yet. Start from the table above.</p>`
            : null}
        ${diff && html`
          <div className="dist" style=${{ marginTop:12 }}>
            ${diff.length===0
              ? html`<div className="numrow"><b>No difference</b><code>identical fingerprints</code><em/></div>`
              : diff.map(x=> html`
                <div key=${x.id} className="numrow">
                  <b>${x.name}</b><code>${x.say}</code>
                  <em><span className="chip" data-tone=${x.tone}>${x.tag}</span></em>
                </div>`)}
          </div>`}
      <//>
    <//>`;
}

